import React, { useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api';
import VariableTextField from './VariableTextField';
import { MARKETING_PRESETS } from '../utils/marketingTemplatePresets';
import { createMarketingForm, newMarketingButton, newMarketingCard, placeholders } from '../utils/marketingTemplateForm';

const HEADER_OPTIONS = ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT', 'LOCATION'];
const BUTTON_LABELS = { QUICK_REPLY: 'Quick reply', URL: 'Visit website', PHONE_NUMBER: 'Call phone', COPY_CODE: 'Copy coupon code', CATALOG: 'View catalog', MPM: 'View items', SPM: 'View product' };

function SampleFields({ text, samples, onChange, label }) {
  const keys = placeholders(text);
  if (!keys.length) return null;
  return <div className="template-form-section template-samples">
    <div className="label">{label} sample values</div>
    <p className="template-advanced-help">Meta uses these examples for review; they are not sent to customers.</p>
    {keys.map(key => <label className="form-group" key={key}>
      <span className="label">Sample for {'{{'}{key}{'}}'}</span>
      <input className="input" required value={samples?.[key] || ''} onChange={event => onChange({ ...samples, [key]: event.target.value })} />
    </label>)}
  </div>;
}

function MediaField({ label, format, filename, onUploaded, onUploadingChange }) {
  const [uploading, setUploading] = useState(false);
  async function upload(file) {
    if (!file) return;
    if (!navigator.onLine) return toast.error('Connect to the internet before uploading media');
    setUploading(true);
    onUploadingChange(1);
    try {
      const data = new FormData();
      data.append('file', file);
      const response = await api.post('/templates/media-handle', data);
      if (response.data?.handle) {
        if (format && response.data.format !== format) throw new Error(`Choose a ${format.toLowerCase()} file for this header`);
        onUploaded(response.data.handle, file.name, response.data.format);
        toast.success(`${file.name} uploaded to Meta`);
      } else throw new Error('Meta did not return a media handle');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not upload media', { duration: 8000 });
    } finally {
      setUploading(false);
      onUploadingChange(-1);
    }
  }
  const accept = format === 'IMAGE' ? 'image/jpeg,image/png' : format === 'VIDEO' ? 'video/mp4' : format === 'DOCUMENT' ? 'application/pdf' : 'image/jpeg,image/png,video/mp4,application/pdf';
  return <div className="form-group">
    <label className="label">{label}</label>
    <input className="input" type="file" accept={accept} disabled={uploading} onChange={event => upload(event.target.files?.[0])} />
    <small className="template-advanced-help">{uploading ? 'Uploading to Meta…' : filename ? `Uploaded: ${filename}` : 'Upload a file for Meta template review.'}</small>
  </div>;
}

function ButtonFields({ buttons, onChange, allowed, max = 10, add = true, title = 'Buttons' }) {
  function update(index, patch) { onChange(buttons.map((item, at) => at === index ? { ...item, ...patch } : item)); }
  return <div className="template-form-section">
    <div className="template-section-heading"><span className="label">{title}</span><small>Up to {max}</small></div>
    {buttons.map((item, index) => <div className="template-button-editor" key={index}>
      <div className="template-button-editor-top">
        <select className="select" aria-label={`${title} ${index + 1} type`} value={item.type}
          disabled={allowed.length === 1} onChange={event => update(index, newMarketingButton(event.target.value))}>
          {allowed.map(type => <option key={type} value={type}>{BUTTON_LABELS[type]}</option>)}
        </select>
        {add && <button type="button" className="btn btn-ghost btn-sm" aria-label={`Remove ${title} ${index + 1}`}
          onClick={() => onChange(buttons.filter((_, at) => at !== index))}>Remove</button>}
      </div>
      {item.type === 'COPY_CODE' ? <label className="form-group"><span className="label">Sample coupon code</span>
        <input className="input" maxLength={20} required value={item.example || ''} onChange={event => update(index, { example: event.target.value })} placeholder="SAVE10" /></label>
        : <label className="form-group"><span className="label">Button label</span>
          <input className="input" maxLength={25} required value={item.text || ''} onChange={event => update(index, { text: event.target.value })} placeholder={BUTTON_LABELS[item.type]} /></label>}
      {item.type === 'URL' && <>
        <VariableTextField label="Website URL" value={item.url || ''} onChange={value => update(index, { url: value })}
          required placeholder="https://your-website.com" maxVariables={1} url
          hint="Optional: insert {{1}} at the end for a personalised link." />
        {placeholders(item.url).length > 0 && <label className="form-group"><span className="label">Sample full URL</span>
          <input className="input" type="url" required value={item.example || ''} onChange={event => update(index, { example: event.target.value })} placeholder="https://your-website.com/offer/123" /></label>}
      </>}
      {item.type === 'PHONE_NUMBER' && <label className="form-group"><span className="label">Phone number with country code</span>
        <input className="input" type="tel" required value={item.phone_number || ''} onChange={event => update(index, { phone_number: event.target.value })} placeholder="+27123456789" /></label>}
    </div>)}
    {add && buttons.length < max && <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange([...buttons, newMarketingButton(allowed[0])])}>+ Add button</button>}
  </div>;
}

export default function MarketingTemplateFields({ form, setForm, onUploadingChange }) {
  const format = form.format;
  const set = (field, value) => setForm(current => ({ ...current, [field]: value }));
  const setCard = (index, patch) => setForm(current => ({ ...current, cards: current.cards.map((item, at) => at === index ? { ...item, ...patch } : item) }));
  const cards = format === 'media_carousel' || format === 'product_carousel';
  const fixedHeader = ['location', 'single_product', 'call_permission', 'media_carousel', 'product_carousel'].includes(format);
  const showHeader = !['call_permission', 'media_carousel', 'product_carousel'].includes(format);
  const showFooter = ['custom', 'coupon', 'catalog', 'multi_product', 'single_product'].includes(format);
  const editableButtons = ['custom', 'coupon'].includes(format);
  const standardButtons = format === 'coupon' ? ['COPY_CODE', 'QUICK_REPLY'] : ['QUICK_REPLY', 'URL', 'PHONE_NUMBER', 'COPY_CODE'];
  const variableStyle = [form.body, form.headerText, ...form.cards.map(item => item.body)]
    .some(text => placeholders(text).some(key => !/^\d+$/.test(key))) ? 'named' : 'numbered';

  return <>
    <div className="form-group">
      <label className="label" htmlFor="marketing-format">Marketing template format</label>
      <select id="marketing-format" className="select" value={format} onChange={event => setForm(current => ({
        ...createMarketingForm(event.target.value), name: current.name, language: current.language, body: current.body, examples: current.examples,
      }))}>
        {Object.entries(MARKETING_PRESETS).map(([key, item]) => <option key={key} value={key}>{item.label}</option>)}
      </select>
      <a href={MARKETING_PRESETS[format].docs} target="_blank" rel="noopener noreferrer" className="template-doc-link">About this format in Meta’s documentation ↗</a>
    </div>

    <div className="template-form-row">
      <label className="form-group"><span className="label">Template name</span>
        <input className="input" required maxLength={512} value={form.name} onChange={event => set('name', event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))} placeholder="summer_offer" /></label>
      <label className="form-group"><span className="label">Language</span>
        <input className="input" required value={form.language} onChange={event => set('language', event.target.value)} placeholder="en_US" /></label>
    </div>

    {showHeader && <div className="template-form-section">
      <div className="label">Header</div>
      {fixedHeader ? <p className="template-advanced-help">{form.headerType === 'LOCATION' ? 'The recipient’s location details are supplied when sending.' : 'The product comes from your connected catalog when sending.'}</p>
        : <select className="select" aria-label="Header type" value={form.headerType} onChange={event => setForm(current => ({ ...current, headerType: event.target.value, mediaHandle: '', mediaName: '' }))}>
          {(format === 'limited_offer' ? ['IMAGE', 'VIDEO'] : HEADER_OPTIONS).map(type => <option key={type} value={type}>{type === 'NONE' ? 'No header' : `${type[0]}${type.slice(1).toLowerCase()} header`}</option>)}
        </select>}
      {form.headerType === 'TEXT' && <>
        <VariableTextField label="Header text" value={form.headerText} onChange={value => set('headerText', value)}
          maxLength={60} maxVariables={1} required style={variableStyle} showCount />
        <SampleFields text={form.headerText} samples={form.headerExamples} onChange={value => set('headerExamples', value)} label="Header" />
      </>}
      {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerType) && <MediaField key={`header-${form.headerType}`} label={`${form.headerType.toLowerCase()} file`} format={form.headerType}
        filename={form.mediaName} onUploadingChange={onUploadingChange}
        onUploaded={(handle, filename) => setForm(current => ({ ...current, mediaHandle: handle, mediaName: filename }))} />}
    </div>}

    {format === 'limited_offer' && <div className="template-form-section">
      <div className="label">Offer details</div>
      <label className="form-group"><span className="label">Offer label</span>
        <input className="input" required maxLength={16} value={form.offerText} onChange={event => set('offerText', event.target.value)} /></label>
      <label className="template-checkbox"><input type="checkbox" checked={form.offerExpires} onChange={event => set('offerExpires', event.target.checked)} /> Show an expiration countdown</label>
      <p className="template-advanced-help">Meta requires the actual expiration time and coupon value when the approved template is sent.</p>
    </div>}

    <VariableTextField label="Message text" value={form.body} onChange={value => set('body', value)} multiline rows={5}
      required maxLength={format === 'limited_offer' ? 600 : 1024} showCount style={variableStyle}
      hint="Click Insert variable to add a personalised value at the cursor."
      placeholder="Hi {{1}}, take a look at our latest offer." />
    <SampleFields text={form.body} samples={form.examples} onChange={value => set('examples', value)} label="Message" />
    {showFooter && <label className="form-group"><span className="label">Footer (optional)</span>
      <input className="input" maxLength={60} value={form.footer} onChange={event => set('footer', event.target.value)} placeholder="Reply STOP to opt out" /></label>}

    {cards && <div className="template-form-section">
      <div className="template-section-heading"><span className="label">{format === 'media_carousel' ? 'Media' : 'Product'} cards</span><small>{form.cards.length}/10</small></div>
      <p className="template-advanced-help">All cards need the same media and button types. Add two to ten cards; choose the product or media for each recipient at send time.</p>
      {form.cards.map((item, index) => <div className="template-card-editor" key={index}>
        <div className="template-section-heading"><strong>Card {index + 1}</strong>
          {form.cards.length > 2 && <button type="button" className="btn btn-ghost btn-sm" onClick={() => set('cards', form.cards.filter((_, at) => at !== index))}>Remove</button>}</div>
        {format === 'media_carousel' && <>
          <select className="select" aria-label={`Card ${index + 1} media type`} value={item.headerType}
            onChange={event => setCard(index, { headerType: event.target.value, mediaHandle: '', mediaName: '' })}>
            <option value="IMAGE">Image</option><option value="VIDEO">Video</option>
          </select>
          <MediaField key={`${index}-${item.headerType}`} label={`Card ${index + 1} ${item.headerType.toLowerCase()}`} format={item.headerType}
            filename={item.mediaName} onUploadingChange={onUploadingChange}
            onUploaded={(handle, filename) => setCard(index, { mediaHandle: handle, mediaName: filename })} />
        </>}
        <VariableTextField label={`Card ${index + 1} text (optional)`} value={item.body} onChange={value => setCard(index, { body: value })}
          multiline rows={2} maxLength={160} showCount style={variableStyle} />
        <SampleFields text={item.body} samples={item.examples} onChange={value => setCard(index, { examples: value })} label={`Card ${index + 1}`} />
        <ButtonFields title={`Card ${index + 1} buttons`} buttons={item.buttons} onChange={value => setCard(index, { buttons: value })}
          allowed={format === 'product_carousel' ? ['SPM', 'URL'] : ['URL', 'QUICK_REPLY', 'PHONE_NUMBER']} max={2} />
      </div>)}
      {form.cards.length < 10 && <button type="button" className="btn btn-ghost btn-sm" onClick={() => set('cards', [...form.cards, newMarketingCard(format)])}>+ Add card</button>}
    </div>}

    {!cards && format !== 'call_permission' && <ButtonFields buttons={form.buttons} onChange={value => set('buttons', value)}
      allowed={format === 'limited_offer' ? ['COPY_CODE', 'URL'] : ['catalog', 'multi_product', 'single_product'].includes(format) ? [form.buttons[0].type] : standardButtons}
      add={editableButtons} max={format === 'coupon' ? 3 : 10} title="Template buttons" />}
    {format === 'call_permission' && <p className="template-advanced-help">The recipient will see Meta’s call-permission choice. This format cannot include other interactive buttons.</p>}
    {['catalog', 'multi_product', 'single_product', 'product_carousel'].includes(format) && <p className="template-advanced-help">A connected Meta catalog is required. Product selections are provided when sending the approved template.</p>}
  </>;
}
